<template lang="pug">
InventoryTab(name="resource" :error="fetchError" @new="newResource" :canView="userCanViewResources" :canAddNew="userCanAddEditResource")
  template(v-slot:filters)
    BriqInput(v-model="query" hideOptionalMarker placeholder="Search" data-test-id="search-resources-input")
    BriqCheckbox.show-inactive(label="Show inactive resources" v-model="showInactive" data-test-id="show-inactive-resources-checkbox")
  template(v-slot:overview)
    BriqDataTable(
      :data="tableData"
      :filter="filter"
      :allowDelete="false"
      :allowEdit="userCanAddEditResource"
      :loading="isFetchingResources"
      @edit="editResource"
      @click="clickResource"
      data-test-id="resources-data-table"
      )
      template(v-slot:Description="{ item }")
        div(:class="item.ParentResourceKey ? 'indent-resouce-name' : ''") {{ item.Description }}
      template(v-slot:ShowOnScheduler="{ item }")
        div {{ displaySchedulingValue(item) }}
      template(v-slot:Capacity="{ item }")
        div(:data-test-id="`${item.Slug}-capacity-cell`") {{ propIfScheduled('Capacity')(item) }}
      template(v-slot:TimeslotMinutes="{ item }")
        div(:data-test-id="`${item.Slug}-timeslot-minutes-cell`") {{ propIfScheduled('TimeslotMinutes')(item) }}
      template(v-slot:OneTimeLeadIn="{ item }")
        div(:data-test-id="`${item.Slug}-one-time-lead-in-cell`") {{ propIfScheduled('OneTimeLeadIn')(item) }}
      template(v-slot:IsActive="{ item }")
        div(:data-test-id="`${item.Slug}-is-active-in-cell`") {{ isActive(item) }}
    ResourceSlideInPanel(
      :focusedItem="focusedItem"
      :sidePanelIsOpen="sidePanelIsOpen"
      :allowEdit="userCanAddEditResource"
      @edit="editResource"
      @close="sidePanelIsOpen = false"
      )
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import BriqDataTable from '@v/components/BriqDataTable.vue'
import ResourceSlideInPanel from './ResourceSlideInPanel.vue'
import InventoryTab from '../InventoryTab.vue'
import Permission from '@v/models/UserPermissionEnum.js'
import { toLower, propEq, always, both, pipe, propSatisfies, includes, either } from 'ramda'
import { propIfScheduled } from './displayResourceUtil.js'

export default {
  name: 'Resources',
  components: { BriqDataTable, InventoryTab, ResourceSlideInPanel },
  data() {
    return {
      query: null,
      sidePanelIsOpen: false,
      focusedItem: null,
      showInactive: false,
    }
  },
  computed: {
    ...mapState('inventory', ['resources', 'isFetchingResources', 'fetchError']),
    ...mapGetters('briqState', ['userHasPermission']),
    tableData() {
      return {
        items: this.filteredResources || [],
        headers: [
          { field: 'Description', label: 'Resource' },
          { field: 'ShowOnScheduler', label: 'Scheduling' },
          { field: 'Capacity', label: 'Capacity' },
          { field: 'TeamSize', label: 'Team Size' },
          { field: 'TimeslotMinutes', label: 'Timeslot Duration' },
          { field: 'OneTimeLeadIn', label: 'One time lead-in' },
          { field: 'ResourceType', label: 'Resource type' },
          { field: 'IsActive', label: 'Active' },
        ],
      }
    },
    userCanAddEditResource() {
      return this.userHasPermission(Permission.API_Admin_Resource_Create)
    },
    userCanViewResources() {
      return this.userHasPermission(Permission.API_Admin_Resource_Index)
    },
    filter() {
      const byQuery = query => {
        if (!query) return always(true)
        const containsQuery = pipe(toLower, includes)(query)
        const propContainsQuery = propSatisfies(pipe(toLower, containsQuery))
        return either(propContainsQuery('Description'), propContainsQuery('ResourceName'))
      }
      const byEnabled = !this.showInactive ? propEq('IsActive', true) : always(true)
      return both(byQuery(this.query), byEnabled)
    },
    filteredResources() {
      if (!this.query || !this.resources) return this.rearrangedResourcesToTreeview
      return this.rearrangedResourcesToTreeview.filter(r =>
        r.Description.toLowerCase().includes(this.query.toLowerCase()),
      )
    },
    rearrangedResourcesToTreeview() {
      if (!this.resources) return []
      // todo: refactor get rid of foreach
      const rearrangedResources = []
      this.resources.forEach(resource => {
        if (!resource.ParentResourceKey) rearrangedResources.push(resource)
        if (resource.ChildResources) {
          resource.ChildResources.forEach(childResource => {
            rearrangedResources.push(this.resources.find(item => item.Key === childResource.Key))
          })
        }
      })
      return rearrangedResources
    },
  },
  methods: {
    ...mapActions('inventory', ['maybeFetchResources']),
    propIfScheduled,
    newResource() {
      this.$router.push({ name: 'CreateResource' })
    },
    editResource({ item, event }) {
      if (event.ctrlKey || event.metaKey) this.$openTab(`inventory/resources/${item.Key}/edit`)
      else this.$router.push({ name: 'EditResource', params: { resourceKey: item.Key } })
    },
    clickResource({ item, event }) {
      if (event.ctrlKey || event.metaKey) this.$openTab(`inventory/resources/${item.Key}/edit`)
      else {
        this.focusedItem = item
        this.sidePanelIsOpen = true
      }
    },
    displaySchedulingValue(item) {
      return item.ResourceType !== 'ResourceGroup'
        ? item.ShowOnScheduler
          ? 'Scheduled'
          : 'Non-scheduled'
        : ''
    },
    isActive(item) {
      return item.IsActive ? 'Yes' : 'No'
    },
  },
  mounted() {
    this.maybeFetchResources()
  },
}
</script>

<style scoped lang="stylus">
.indent-resouce-name
  padding-left 15px

.show-inactive
  min-width 300px
</style>
